import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { pathgen } from 'object-path-generator';
import { Button, FunctionalLoader, Page, PageContentHeader, PageEmptyState, Typography, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import ErrorBoundaryPage from '~/components/ErrorBoundaryPage/ErrorBoundaryPage';
import LoadingIndicator from '~/components/LoadingIndicator';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import ActivityLog from '~/pages/AgreementMinisite/ActivityLog/ActivityLog';
import { buildMinisiteTabs } from '~/pages/AgreementMinisite/AgreementMinisite';
import AgreementMinisiteTopBar from '~/pages/AgreementMinisite/AgreementMinisiteTopBar/AgreementMinisiteTopBar';
import AgreementView from '~/pages/AgreementMinisite/AgreementView/AgreementView';
import BillingHub from '~/pages/AgreementMinisite/BillingHub/BillingHub';
import InvoicesView from '~/pages/AgreementMinisite/InvoicesView/InvoicesView';
import PaymentMethods from '~/pages/AgreementMinisite/PaymentMethods/PaymentMethods';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import RestfulWrapper from '../../components/RestfulWrapper/RestfulWrapper';
import SeeWhatItsLike from '../../components/SeeWhatItsLike';
import { BillingHubUrlParams } from '../AgreementMinisite/BillingHub/BillingHub.utils';
import { AdHocAgreementEmptyState } from './AdHocAgreementEmptyState/AdHocAgreementEmptyState';
import { BulkBillingModal } from './BulkBillingModal/BulkBillingModal';
import { GBHMapEntitiesCallout } from './GBHMapEntitiesCallout/GBHMapEntitiesCallout';
import styles from './GlobalBillingHub.module.scss';
import { getClientOptions, getDefaultRelationshipId, getGroupedAgreementOptions } from './GlobalBillingHub.utils';
import { GlobalBillingHubHeader } from './GlobalBillingHubHeader/GlobalBillingHubHeader';
import GlobalBillingHubEmptyStateIcon from './img/GlobalBillingHubEmptyStateIcon';
import img1 from './img/globalBillingHubPreview1.png';
import img2 from './img/globalBillingHubPreview2.png';
const seeWhatItsLikeSteps = [
    {
        title: 'A single place for managing client billing',
        subtitle: 'Easily view and update upcoming invoices for all of your clients in one place.',
        image: {
            src: img1,
            width: 850,
            height: 497
        }
    },
    {
        title: 'Keep payments on autopilot, even when making changes',
        subtitle: "Add or remove services, charges, and credits and we'll make sure to automate everything, including client reminders.",
        image: {
            src: img2,
            width: 850,
            height: 505
        }
    }
];
function BillingHubRelationshipFetcher(param) {
    let { relationshipId } = param;
    const isVendor = useStore(selectors.user.isVendor);
    const { relationship, isLoading, error, refresh: fetchRelationship } = useRelationship(relationshipId);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    useOnMount(()=>{
        fetchRelationship({
            silent: true
        });
    });
    if (error) throw makeSentryIgnoredError('Error fetching relationship');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FunctionalLoader, {
                show: isLoading
            }),
            relationship && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.calloutContainer,
                        children: /*#__PURE__*/ _jsx(PageContentHeader, {
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: styles.callouts,
                                children: [
                                    showQboIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.QBO
                                    }),
                                    showXeroIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.Xero
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(BillingHub, {
                        isVendor: isVendor,
                        agreement: relationship,
                        refreshAgreement: fetchRelationship
                    })
                ]
            })
        ]
    });
}
function GlobalBillingHubTabbedView(param) {
    let { relationshipId } = param;
    const isVendor = useStore(selectors.user.isVendor);
    const { relationship, isLoading, error, refresh } = useRelationship(relationshipId);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const history = useHistory();
    const location = useLocation();
    const basePath = routes.globalBillingHub;
    const { t } = useLocaleKeys('pages.agreementMinisite');
    const minisiteTabs = buildMinisiteTabs(t.tabs);
    useOnMount(()=>{
        refresh({
            silent: true
        });
    });
    useOnMount(()=>{
        const knownTabUrls = Object.values(minisiteTabs).map((t)=>t.url);
        const currentSegment = location.pathname.slice(basePath.length + 1).split('/')[0];
        if (!currentSegment || !knownTabUrls.includes(currentSegment)) {
            history.replace("".concat(basePath, "/").concat(minisiteTabs.billingHub.url).concat(location.search));
        }
    });
    if (error) throw makeSentryIgnoredError('Error fetching relationship');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FunctionalLoader, {
                show: isLoading
            }),
            relationship && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.calloutContainer,
                        children: /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.callouts,
                                    children: [
                                        showQboIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                            services: relationship.services,
                                            clientID: relationship.clientContactId,
                                            refreshAgreement: refresh,
                                            accounting: Accounting.QBO
                                        }),
                                        showXeroIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                            services: relationship.services,
                                            clientID: relationship.clientContactId,
                                            refreshAgreement: refresh,
                                            accounting: Accounting.Xero
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.tabBarWrapper,
                                    children: /*#__PURE__*/ _jsx(AgreementMinisiteTopBar, {
                                        relationship: relationship,
                                        tabs: minisiteTabs
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(ErrorBoundaryPage, {
                        bounded: true,
                        children: [
                            /*#__PURE__*/ _jsx(Route, {
                                path: "".concat(basePath, "/").concat(minisiteTabs.billingHub.url),
                                exact: true,
                                children: /*#__PURE__*/ _jsx(BillingHub, {
                                    isVendor: isVendor,
                                    agreement: relationship,
                                    refreshAgreement: refresh
                                })
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: "".concat(basePath, "/").concat(minisiteTabs.agreement.url),
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: styles.tabContent,
                                    children: relationshipModel.isAdHoc(relationship) ? /*#__PURE__*/ _jsx(AdHocAgreementEmptyState, {
                                        clientContactId: relationship.clientContactId
                                    }) : /*#__PURE__*/ _jsx(AgreementView, {
                                        relationship: relationship,
                                        isVendor: isVendor,
                                        refresh: refresh
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: "".concat(basePath, "/").concat(minisiteTabs.paymentMethods.url),
                                children: /*#__PURE__*/ _jsx(PaymentMethods, {
                                    isVendor: isVendor,
                                    agreement: relationship,
                                    onRefreshAgreement: refresh
                                })
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: "".concat(basePath, "/").concat(minisiteTabs.invoices.url),
                                children: /*#__PURE__*/ _jsx(InvoicesView, {
                                    agreement: relationship,
                                    viewMode: isVendor ? 'vendor' : 'customer',
                                    refreshAgreement: refresh
                                })
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: "".concat(basePath, "/").concat(minisiteTabs.activity.url),
                                render: (props)=>/*#__PURE__*/ _jsx(ActivityLog, _object_spread({
                                        agreement: relationship
                                    }, props))
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export function GlobalBillingHubWithSummaries(props) {
    const { t: gbhTexts } = useLocaleKeys('pages.globalBillingHub');
    const { agreements, BH } = props;
    const { AD_HOC_INVOICING: isAdHocInvoicingEnabled } = useFeatureFlags();
    const { urlState: { clientId, relationshipId }, changeUrlState } = useStateFromUrl({
        [BillingHubUrlParams.ClientId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.RelationshipId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.SelectedDate]: {
            defaultValue: ''
        }
    });
    const scrollElRef = useRef(null);
    const [groupedOptions, setGroupedOptions] = useState(()=>getGroupedAgreementOptions(agreements, clientId));
    const agreementOptions = useMemo(()=>[
            ...groupedOptions.agreements,
            ...groupedOptions.adHocRelationships
        ], [
        groupedOptions
    ]);
    const clientOptions = useMemo(()=>getClientOptions(agreements), [
        agreements
    ]);
    const selectedClient = useMemo(()=>clientId ? clientOptions.find((opt)=>opt.id === clientId) : null, [
        clientId,
        clientOptions
    ]);
    // Clear selected contact if it doesn't exist in the options.
    useEffect(()=>{
        if (clientId && clientOptions.every((o)=>o.id !== clientId)) changeUrlState({
            clientId: ''
        });
    }, [
        clientId,
        clientOptions
    ]);
    // Clear selected agreement if it doesn't exist in the options.
    useEffect(()=>{
        if (relationshipId && agreementOptions.every((o)=>o.id !== relationshipId)) changeUrlState({
            relationshipId: ''
        });
    }, [
        relationshipId,
        agreementOptions
    ]);
    // Automatically populate relationshipId if it can be populated.
    useEffect(()=>{
        if (clientId && !relationshipId && agreementOptions.length) changeUrlState({
            relationshipId: getDefaultRelationshipId(agreementOptions)
        });
    }, [
        clientId,
        relationshipId,
        agreementOptions
    ]);
    const { showModal } = useOverlayContext();
    const { logBI } = useStoreActions();
    const handleBulkBilling = ()=>{
        logBI(18700);
        void showModal({
            biName: 'bulk_billing_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BulkBillingModal, {
                    dismissModal: dismissModal
                });
            }
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        header: /*#__PURE__*/ _jsx(GlobalBillingHubHeader, {
            setGroupedOptions: setGroupedOptions,
            changeUrlState: changeUrlState,
            agreements: agreements,
            clientOptions: clientOptions,
            selectedClient: selectedClient,
            groupedOptions: groupedOptions,
            relationshipId: relationshipId
        }),
        ref: scrollElRef,
        actions: !clientId && /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'open_bulk_billing'
            },
            skin: "primaryOutline",
            testId: GlobalBillingHubTestIds.BulkBillingButton(),
            onClick: handleBulkBilling,
            children: gbhTexts.billClientsInBulk()
        }),
        children: relationshipId ? BH || (isAdHocInvoicingEnabled ? /*#__PURE__*/ _jsx(GlobalBillingHubTabbedView, {
            relationshipId: relationshipId
        }, relationshipId) : /*#__PURE__*/ _jsx(BillingHubRelationshipFetcher, {
            relationshipId: relationshipId
        }, relationshipId)) : /*#__PURE__*/ _jsx(Typography, {
            testId: "gbh-no-agreement",
            className: styles.unselected,
            variant: "paragraph1",
            children: gbhTexts.pleaseChooseClient()
        })
    });
}
export const GlobalBillingHubTestIds = pathgen('GlobalBillingHubTestIds');
export default function GlobalBillingHub() {
    const { t: texts } = useLocaleKeys('pages.globalBillingHub');
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    useSetDocumentTitle('Billing');
    const fetchAgreementsAndAdHoc = ()=>{
        if (!businessId) return Promise.resolve([]);
        return api.relationship.getSummaries({
            businessId,
            type: 'agreementAndAdHoc',
            isVendor
        });
    };
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchAgreementsAndAdHoc,
        renderLoader: ()=>/*#__PURE__*/ _jsx(Page, {
                children: /*#__PURE__*/ _jsx(LoadingIndicator, {})
            }),
        renderChild: (param)=>{
            let { data } = param;
            return data.length ? /*#__PURE__*/ _jsx(GlobalBillingHubWithSummaries, {
                agreements: data
            }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: "gbh-empty",
                illustration: /*#__PURE__*/ _jsx(GlobalBillingHubEmptyStateIcon, {}),
                title: texts.emptyTitle(),
                message: texts.emptyMessage(),
                actions: /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                    steps: seeWhatItsLikeSteps
                })
            });
        }
    });
}
